import {Button, Divider, HStack, Text, Textarea, Tooltip, useTheme, VStack} from '@chakra-ui/react'
import {faCopy} from '@fortawesome/pro-regular-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import cedille from '../../public/assets/generatedByCedille.svg'
import ResizeTextarea from 'react-textarea-autosize'
import useTranslation from 'next-translate/useTranslation'
import {useSkill} from '../../hooks/useSkill'
import {diffChars, Change} from 'diff'
import {useMemo, useRef} from 'react'

interface Props {
  output: string
  input: string
  showDiff?: boolean
}

const Difference = ({before, after}: {before: string; after: string}) => {
  const all = useMemo(() => diffChars(before, after), [before, after])
  const getColor = (d: Change) => {
    if (d.added) return 'green.500'
    if (d.removed) return 'red.500'
    return 'gray.400'
  }
  return (
    <Text>
      {all.map((d, i) => (
        <Text key={d.value + i} as='span' color={getColor(d)} fontSize='sm'>
          {d.value}
        </Text>
      ))}
    </Text>
  )
}

const SkillOutput = ({output, input, showDiff}: Props) => {
  const theme = useTheme()
  const ref = useRef<HTMLTextAreaElement>(null)
  const {t} = useTranslation('skill')

  const onCopy = async () => {
    // noinspection JSIgnoredPromiseFromCall
    await navigator.clipboard.writeText(ref.current?.value ?? output)

    const showToast = await import('../../utils/showToast').then(mod => mod.showToast)
    showToast(t('common:clipboard_onClick'), 'success')
  }

  const {isToxic} = useSkill()
  return (
    <VStack
      p={5}
      spacing={5}
      w='full'
      alignItems='start'
      borderRadius={theme.radii.medium}
      background={theme.colors.layers.appLevel2}>
      <HStack spacing={8} w='full' justify='space-between'>
        <HStack>
          <Image src={cedille} alt='cedille-logo' />
          <Text variant='mediumBold' color={theme.colors.text.grey}>
            {t('block_skill_output_generated')}
          </Text>
        </HStack>
        <HStack spacing={6}>
          <Tooltip label={t('common:clipboard_tooltip')} placement='top'>
            <Button variant='textIcon' onClick={onCopy} aria-label='Copy content to clipboard'>
              <FontAwesomeIcon icon={faCopy} />
            </Button>
          </Tooltip>
        </HStack>
      </HStack>
      <Divider />
      {showDiff && <Difference before={input} after={output} />}
      <Textarea
        ref={ref}
        p={0}
        overflow='hidden'
        w='full'
        resize='none'
        background={theme.colors.transparent}
        color={isToxic ? theme.colors.brand.error : theme.colors.text.basic}
        variant='large'
        fontSize={theme.components.Text.variants.large.fontSize}
        as={ResizeTextarea}
        defaultValue={output ?? ''}
      />
    </VStack>
  )
}

export default SkillOutput
